import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Select, MenuItem, FormControl, FormControlLabel,
  Checkbox, Radio, RadioGroup, Typography, Box,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import NatalChartSVG from './NatalChartSVG';
import LOCATIONS from '../constants/locations';

const DEFAULT_SKIP_DURATION = 4;   // minutes
const DEFAULT_FRAME_DELAY   = 1;   // seconds
const DEFAULT_START_HOUR    = 6;   // 06:30 local
const DEFAULT_START_MINUTE  = 30;

const pad2 = (n) => String(n).padStart(2, '0');

const formatTime = (h, m) => `${pad2(h)}:${pad2(m)}`;

const IntradayAnimationDialog = ({ open, onClose }) => {
  const { api, token } = useAuth();

  // Location state
  const [selectedLocation, setSelectedLocation] = useState('San Francisco');
  const [longitude, setLongitude] = useState('-122.42');
  const [latitude, setLatitude]   = useState('37.77');
  const [utcOffset, setUtcOffset] = useState(-8);
  const [tz, setTz]               = useState('PST');

  // Date / time state
  const [date, setDate]       = useState('');
  const [hour, setHour]       = useState(DEFAULT_START_HOUR);
  const [minute, setMinute]   = useState(DEFAULT_START_MINUTE);

  // Animation parameters
  const [skipDuration, setSkipDuration] = useState(DEFAULT_SKIP_DURATION);
  const [frameDelay, setFrameDelay]     = useState(DEFAULT_FRAME_DELAY);
  const [direction, setDirection]       = useState('forward');
  const [animating, setAnimating]       = useState(false);

  // Chart state
  const [chartData, setChartData]             = useState(null);
  const [drawAspectLines, setDrawAspectLines] = useState(false);
  const [error, setError]                     = useState('');

  // Planet nakshatra/charan flash tracking — persists until next fetch
  const [flashPlanets, setFlashPlanets] = useState(null); // Set<string> of abbrs
  const prevNakMapRef = useRef({}); // { abbr: 'NakAbbr/pada' }

  // Pre-computed kendra aspects (populated via button)
  const [kendraLog, setKendraLog] = useState([]);   // Asc aspects
  const [mcLog, setMcLog] = useState([]);             // MC aspects
  const [aspectsLoading, setAspectsLoading] = useState(false);
  const [aspectsDate, setAspectsDate] = useState(''); // date for which aspects were computed

  // Refs for animation interval
  const intervalRef = useRef(null);
  const fetchSeqRef = useRef(0);   // sequence counter to discard stale responses
  const animatingRef = useRef(false); // guard against double-start during async precompute
  const animStateRef = useRef({ date: '', hour: DEFAULT_START_HOUR, minute: DEFAULT_START_MINUTE });
  const directionRef = useRef(direction);

  // Initialise date to today when dialog opens; full reset on reopen
  useEffect(() => {
    if (open) {
      if (!date) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = pad2(today.getMonth() + 1);
        const dd = pad2(today.getDate());
        setDate(`${yyyy}-${mm}-${dd}`);
      }
    } else {
      // Dialog closed — full cleanup
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      animatingRef.current = false;
      setAnimating(false);
      setChartData(null);
      setFlashPlanets(null);
      prevNakMapRef.current = {};
      setKendraLog([]);
      setMcLog([]);
      setAspectsLoading(false);
      setAspectsDate('');
      setError('');
      setHour(DEFAULT_START_HOUR);
      setMinute(DEFAULT_START_MINUTE);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep refs in sync with state for use inside interval callback
  useEffect(() => {
    animStateRef.current = { date, hour, minute };
  }, [date, hour, minute]);
  useEffect(() => { directionRef.current = direction; }, [direction]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Location change handler
  const handleLocationChange = (locLabel) => {
    setSelectedLocation(locLabel);
    if (locLabel === 'Custom') return;
    const loc = LOCATIONS.find(l => l.label === locLabel);
    if (loc) {
      setLongitude(loc.lon);
      setLatitude(loc.lat);
      setUtcOffset(loc.utcOffset);
      setTz(loc.tz);
    }
  };

  // Fetch chart for a given date/time
  const fetchChart = useCallback(async (d, h, m) => {
    if (!d || longitude === '' || latitude === '') return;
    const seq = ++fetchSeqRef.current;
    try {
      const res = await api.post('/api/natal-chart', {
        date: d,
        time: formatTime(h, m),
        longitude: Number(longitude),
        latitude: Number(latitude),
        utcOffset: utcOffset ?? -5,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Discard stale responses from earlier requests
      if (seq !== fetchSeqRef.current) return;
      if (res.data && !res.data.error) {
        // Detect which planets changed nakshatra/charan since last frame
        const changed = new Set();
        const newNakMap = {};
        (res.data.planets || []).forEach(p => {
          if (!p.nakshatraAbbr) return;
          const key = `${p.nakshatraAbbr}/${p.nakshatraPada || ''}`;
          newNakMap[p.abbr] = key;
          const prev = prevNakMapRef.current[p.abbr];
          if (prev && prev !== key) changed.add(p.abbr);
        });
        prevNakMapRef.current = newNakMap;
        setFlashPlanets(changed.size > 0 ? changed : null);
        setChartData(res.data);
        setError('');
      } else {
        setError(res.data?.error || 'Unknown error');
      }
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err.response?.data?.error || err.message);
    }
  }, [api, token, longitude, latitude, utcOffset]);

  // Step one frame forward or backward
  const stepOnce = useCallback((dir) => {
    const s = animStateRef.current;
    let totalMinutes = s.hour * 60 + s.minute;
    const delta = dir === 'forward' ? skipDuration : -skipDuration;
    totalMinutes += delta;

    let newDate = s.date;
    if (totalMinutes >= 1440) {
      // crossed midnight forward
      totalMinutes -= 1440;
      newDate = advanceDate(newDate, 1);
    } else if (totalMinutes < 0) {
      // crossed midnight backward
      totalMinutes += 1440;
      newDate = advanceDate(newDate, -1);
    }

    const newHour = Math.floor(totalMinutes / 60);
    const newMin  = totalMinutes % 60;

    setDate(newDate);
    setHour(newHour);
    setMinute(newMin);
    // Update ref immediately so next tick sees the new values
    animStateRef.current = { date: newDate, hour: newHour, minute: newMin };

    fetchChart(newDate, newHour, newMin);
  }, [skipDuration, fetchChart]);

  // Single step buttons
  const handleStepForward = () => stepOnce('forward');
  const handleStepBackward = () => stepOnce('backward');

  // Pre-compute all Kendra aspects (Asc + MC) for 24 hours starting 06:30 PST
  const precomputeAspects = useCallback(async () => {
    if (!date || longitude === '' || latitude === '') return;
    setAspectsLoading(true);
    setKendraLog([]);
    setMcLog([]);
    setAspectsDate(date);
    const PST_OFFSET = -8;
    const pstStartMin = 6 * 60 + 30; // 06:30 PST
    const localStartMin = pstStartMin + (utcOffset - PST_OFFSET) * 60;
    const STEP = 1; // 1-minute resolution
    const TOTAL = 24 * 60; // 24 hours
    const BATCH = 30;

    // Build time slots
    const slots = [];
    for (let i = 0; i < TOTAL; i += STEP) {
      let lm = localStartMin + i;
      let dayOff = 0;
      while (lm >= 1440) { lm -= 1440; dayOff++; }
      while (lm < 0) { lm += 1440; dayOff--; }
      slots.push({
        date: dayOff !== 0 ? advanceDate(date, dayOff) : date,
        hour: Math.floor(lm / 60),
        minute: lm % 60,
        pstMin: pstStartMin + i,
      });
    }

    // Fetch in batches
    const allData = [];
    for (let b = 0; b < slots.length; b += BATCH) {
      const chunk = slots.slice(b, b + BATCH);
      const results = await Promise.all(
        chunk.map(s =>
          api.post('/api/natal-chart', {
            date: s.date,
            time: formatTime(s.hour, s.minute),
            longitude: Number(longitude),
            latitude: Number(latitude),
            utcOffset: utcOffset ?? -5,
          }, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.data)
            .catch(() => null)
        )
      );
      results.forEach((d, i) => allData.push({ data: d, slot: chunk[i] }));
    }

    // Detect aspects via crossover: check if Asc/MC crossed (planet + offset)
    // between consecutive minutes.  After detecting a crossing, verify the actual
    // angular distance matches the expected aspect angle (within 2°) to guard
    // against false positives from data anomalies.
    const OFFSETS = [0, 90, 180, 270];
    const ascLog = [];
    const mcAspLog = [];
    let prevAsc = null;
    let prevMc  = null;

    const minArc = (a, b) => {
      const d = ((a - b) % 360 + 360) % 360;
      return d > 180 ? 360 - d : d;
    };

    allData.forEach(({ data, slot }) => {
      if (!data || data.error) { prevAsc = null; prevMc = null; return; }
      const currAsc = data.ascendantLon;
      const currMc  = data.mcLon;
      const fmtTime = () => {
        const rawMin = ((slot.pstMin % 1440) + 1440) % 1440;
        return `${pad2(Math.floor(rawMin / 60))}:${pad2(rawMin % 60)}`;
      };
      (data.planets || []).forEach(p => {
        if (p.siderealLon == null) return;
        OFFSETS.forEach(off => {
          const target = (p.siderealLon + off) % 360;
          const expectedAngle = off === 270 ? 90 : off; // 270° offset = 90° min-arc
          // Asc crossing + verification
          if (prevAsc != null && currAsc != null &&
              crossedLongitude(prevAsc, currAsc, target)) {
            const actualDist = minArc(currAsc, p.siderealLon);
            if (Math.abs(actualDist - expectedAngle) <= 2) {
              ascLog.push({ time: fmtTime(), label: `${p.abbr} ${expectedAngle}\u00b0` });
            }
          }
          // MC crossing + verification
          if (prevMc != null && currMc != null &&
              crossedLongitude(prevMc, currMc, target)) {
            const actualDist = minArc(currMc, p.siderealLon);
            if (Math.abs(actualDist - expectedAngle) <= 2) {
              mcAspLog.push({ time: fmtTime(), label: `${p.abbr} ${expectedAngle}\u00b0` });
            }
          }
        });
      });
      prevAsc = currAsc;
      prevMc  = currMc;
    });

    setKendraLog(ascLog);
    setMcLog(mcAspLog);
    setAspectsLoading(false);
  }, [api, token, date, longitude, latitude, utcOffset]);

  // Start animation
  const handleAnimate = () => {
    if (animating || animatingRef.current) return;
    animatingRef.current = true;
    // Always clear any lingering interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setAnimating(true);
    fetchChart(date, hour, minute);
    intervalRef.current = setInterval(() => {
      stepOnce(directionRef.current);
    }, Math.max(200, frameDelay * 1000));
  };

  // Stop animation
  const handleStop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    animatingRef.current = false;
    setAnimating(false);
  };

  // Reset to 06:00
  const handleReset = () => {
    handleStop();
    setHour(DEFAULT_START_HOUR);
    setMinute(DEFAULT_START_MINUTE);
    setKendraLog([]);
    setMcLog([]);
    setAspectsDate('');
  };

  return (
    <Dialog open={open} onClose={() => { handleStop(); onClose(); }}
      maxWidth={false} fullWidth
      PaperProps={{ sx: { maxHeight: '95vh', height: '95vh', maxWidth: '98vw', width: 2200 } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 20, py: 1 }}>
        Intraday Sky Map
      </DialogTitle>

      <DialogContent sx={{ p: 1, pt: 0, overflow: 'auto', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start', gap: 5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{ mt: 12, flexShrink: 0 }} />
        <NatalChartSVG chartData={chartData} drawAspectLines={drawAspectLines} flashPlanets={flashPlanets}>
          {/* ── Center panel controls ── */}
          <Box sx={{ width: '100%', px: 1.5, py: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* Current time display — top right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Typography sx={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#1565c0', fontFamily: 'monospace', letterSpacing: 1 }}>
                {formatTime(hour, minute)}&nbsp;&nbsp;{tz}
              </Typography>
            </Box>

            {/* Row 1: Date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: '0.9rem', minWidth: 55, textAlign: 'right' }}>Date</Typography>
              <TextField size="small" type="date" value={date}
                onChange={e => setDate(e.target.value)} sx={{ width: 155 }} />
            </Box>

            {/* Row 2: Location + UTC */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: '0.9rem', minWidth: 55, textAlign: 'right' }}>Location</Typography>
              <FormControl size="small" sx={{ width: 140 }}>
                <Select value={selectedLocation} onChange={e => handleLocationChange(e.target.value)}>
                  {LOCATIONS.map(l => <MenuItem key={l.label} value={l.label}>{l.label}</MenuItem>)}
                  <MenuItem value="Custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: '0.9rem' }}>UTC</Typography>
              <TextField size="small" type="number" value={utcOffset}
                onChange={e => { setUtcOffset(Number(e.target.value)); setSelectedLocation('Custom'); }}
                inputProps={{ step: 0.5, min: -12, max: 14 }} sx={{ width: 60 }} />
            </Box>

            {/* Row 3: Longitude + Latitude */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: '0.9rem', minWidth: 55, textAlign: 'right' }}>Lon</Typography>
              <TextField size="small" type="number" value={longitude}
                onChange={e => { setLongitude(e.target.value); setSelectedLocation('Custom'); }}
                inputProps={{ min: -180, max: 180, step: 0.01 }} sx={{ width: 90 }} />
              <Typography sx={{ fontSize: '0.9rem' }}>Lat</Typography>
              <TextField size="small" type="number" value={latitude}
                onChange={e => { setLatitude(e.target.value); setSelectedLocation('Custom'); }}
                inputProps={{ min: -90, max: 90, step: 0.01 }} sx={{ width: 80 }} />
            </Box>

            {/* Row 4: Skip Duration + Frame Delay */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Skip (min)</Typography>
              <TextField size="small" type="number" value={skipDuration}
                onChange={e => setSkipDuration(Math.max(1, parseInt(e.target.value, 10) || 1))}
                inputProps={{ min: 1, max: 120 }} sx={{ width: 65 }} />
              <Typography sx={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Delay (sec)</Typography>
              <TextField size="small" type="number" value={frameDelay}
                onChange={e => setFrameDelay(Math.max(0.2, parseFloat(e.target.value) || 0.2))}
                inputProps={{ min: 0.2, max: 10, step: 0.1 }} sx={{ width: 65 }} />
            </Box>

            {/* Row 5: Direction radio */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography sx={{ fontSize: '0.9rem' }}>Direction</Typography>
              <RadioGroup row value={direction} onChange={e => setDirection(e.target.value)} sx={{ ml: 0.5 }}>
                <FormControlLabel value="forward" control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Forward</Typography>} />
                <FormControlLabel value="backward" control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Backward</Typography>} />
              </RadioGroup>
            </Box>

            {/* Row 6: Animate / Pause / Step buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Button variant="outlined" size="small" onClick={handleStepBackward}
                disabled={animating}
                sx={{ minWidth: 32, px: 0.5 }}>
                ◀
              </Button>
              {!animating ? (
                <Button variant="contained" size="small" onClick={handleAnimate}
                  sx={{ minWidth: 90, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
                  ANIMATE
                </Button>
              ) : (
                <Button variant="contained" size="small" onClick={handleStop}
                  sx={{ minWidth: 90, backgroundColor: '#e65100', '&:hover': { backgroundColor: '#bf360c' } }}>
                  Pause
                </Button>
              )}
              <Button variant="outlined" size="small" onClick={handleStepForward}
                disabled={animating}
                sx={{ minWidth: 32, px: 0.5 }}>
                ▶
              </Button>
              <Button variant="outlined" size="small" onClick={handleReset}
                sx={{ minWidth: 55 }}>
                RESET
              </Button>
            </Box>

            {/* Error message */}
            {error && (
              <Typography sx={{ fontSize: '0.8rem', color: 'red', textAlign: 'center' }}>{error}</Typography>
            )}

            {/* Spacer to push checkbox to bottom */}
            <Box sx={{ flex: 1 }} />

            {/* Draw Aspect Lines checkbox — centered at bottom */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pb: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={drawAspectLines}
                  onChange={e => setDrawAspectLines(e.target.checked)} />}
                label={<Typography sx={{ fontSize: '0.9rem' }}>Draw Aspect Lines</Typography>}
              />
            </Box>
          </Box>
        </NatalChartSVG>
        </Box>

        {/* Right-side aspect tables */}
        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', mt: 2, gap: 1 }}>
          <Button variant="contained" size="small" onClick={precomputeAspects}
            disabled={aspectsLoading}
            sx={{ alignSelf: 'center', textTransform: 'none', fontSize: '0.85rem' }}>
            {aspectsLoading ? 'Computing…' : 'Compute Aspects'}
          </Button>
          {aspectsDate && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#555' }}>
              {aspectsDate} · {selectedLocation !== 'Custom' ? selectedLocation : `${latitude}, ${longitude}`} · UTC{utcOffset >= 0 ? '+' : ''}{utcOffset}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Asc aspects table */}
            <Box sx={{ width: 250 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 720, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.78rem', py: 0.5, width: 50 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.78rem', py: 0.5 }}>Kendra w/ Asc</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aspectsLoading ? (
                      <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', color: '#1565c0', fontSize: '0.8rem' }}>Computing…</TableCell></TableRow>
                    ) : kendraLog.length === 0 ? (
                      <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', color: '#999', fontSize: '0.75rem' }}>Click button to compute</TableCell></TableRow>
                    ) : kendraLog.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', py: 0.2 }}>{entry.time}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', py: 0.2 }}>{entry.label}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {/* MC aspects table */}
            <Box sx={{ width: 250 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 720, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.78rem', py: 0.5, width: 50 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.78rem', py: 0.5 }}>Kendra w/ MC</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aspectsLoading ? (
                      <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', color: '#1565c0', fontSize: '0.8rem' }}>Computing…</TableCell></TableRow>
                    ) : mcLog.length === 0 ? (
                      <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', color: '#999', fontSize: '0.75rem' }}>Click button to compute</TableCell></TableRow>
                    ) : mcLog.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', py: 0.2 }}>{entry.time}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', py: 0.2 }}>{entry.label}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', py: 1 }}>
        <Button onClick={() => { handleStop(); onClose(); }} variant="contained" color="inherit">
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper: did the Asc/MC sweep past a target longitude between prev and curr?
// Uses the "short arc" (≤180°) between prev and curr to handle 360°→0° wrap.
function crossedLongitude(prev, curr, target) {
  prev   = ((prev   % 360) + 360) % 360;
  curr   = ((curr   % 360) + 360) % 360;
  target = ((target % 360) + 360) % 360;
  if (prev === curr) return false;

  const lo = Math.min(prev, curr);
  const hi = Math.max(prev, curr);
  if (hi - lo <= 180) {
    return target > lo && target < hi;
  }
  // Short arc crosses 0° (e.g. 350→010)
  return target > hi || target < lo;
}

// Helper: advance a yyyy-MM-dd date by N days
function advanceDate(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}

IntradayAnimationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default IntradayAnimationDialog;
